import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Button, Flex, Form, Input, Modal, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import isNumber from 'lodash/isNumber';
import moment from 'moment';
import { signOut } from 'next-auth/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { ArrowRight } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import {
  actionDimensionIds,
  DATE_FORMATS,
  MATOMO_ACTION,
  MATOMO_CATEGORY,
  MATOMO_NAME,
  ROUTES,
  SIGNUP_METHOD,
  STATIC_DATA_KEYS,
  SUBSCRIPTION_PLAN_KEYS,
  SUBSCRIPTION_STATUS,
  TRACK_VALUE
} from '../../../../common/constants';
import useDeviceInfo from '../../../../common/useDeviceInfo';
import useLoginWithToken from '../../../../common/useLoginWithToken';
import useNetworkType from '../../../../common/useNetworkInfo';
import useStaticData from '../../../../common/useStaticData';
import { encode, getUserCommonDimensions } from '../../../../common/utils';
import {
  CANCEL_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION
} from '../../../../graphql/Mutations';
import {
  GET_IS_EXISTING_USER,
  PREVIEW_SUBSCRIPTION
} from '../../../../graphql/Queries';
import { authClient } from '../../../../lib/apollo';
import { gaEvent, logSubscriptionEvent } from '../../../../lib/gtag';
import { useApp } from '../../../../providers/AppContext';

export const FREQUENCIES = {
  MONTHLY: 'MONTHLY',
  ANNUALLY: 'ANNUALLY'
};

const initialEmailModalValues = {
  email: ''
};

export const currencyFormat = (value) =>
  isNumber(value)
    ? new Intl.NumberFormat('en', {
        style: 'currency',
        currency: 'USD'
      }).format(value)
    : '-';

function BestValue(props) {
  return (
    <svg
      width="94"
      height="95"
      viewBox="0 0 94 95"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter="url(#filter0_d_528_5821)">
        <path
          d="M35.918 12.0886C36.6189 10.9063 38.1356 10.4999 39.3337 11.1734L45.1986 14.4702C46.0001 14.9208 46.9829 14.901 47.7655 14.4185L53.493 10.8882C54.663 10.167 56.1948 10.512 56.9427 11.6651L60.6038 17.31C61.104 18.0813 61.9835 18.5205 62.9007 18.4571L69.6127 17.9928C70.9839 17.8979 72.1799 18.9153 72.3062 20.2839L72.9246 26.9835C73.0092 27.899 73.5837 28.6966 74.4254 29.0667L80.5844 31.7748C81.8425 32.328 82.4288 33.7846 81.9046 35.0552L79.3387 41.2748C78.9881 42.1247 79.1262 43.098 79.6995 43.8168L83.8944 49.0769C84.7514 50.1515 84.5936 51.7137 83.539 52.5951L78.3766 56.9099C77.6712 57.4995 77.3412 58.4255 77.5147 59.3283L78.7847 65.9355C79.0441 67.2852 78.1783 68.5951 76.8349 68.8855L70.2587 70.307C69.3601 70.5012 68.6375 71.1678 68.3716 72.0479L66.4256 78.4884C66.0281 79.8041 64.6527 80.5616 63.3283 80.1944L56.8447 78.3969C55.9587 78.1513 55.0092 78.4057 54.3648 79.0614L49.6486 83.8599C48.6852 84.8401 47.1153 84.8717 46.1132 83.9311L41.2077 79.3264C40.5373 78.6972 39.5783 78.4812 38.703 78.7623L32.2971 80.8194C30.9884 81.2396 29.5837 80.5381 29.1335 79.2395L26.9298 72.8825C26.6286 72.0139 25.8799 71.377 24.9741 71.2191L18.346 70.0636C16.992 69.8275 16.0742 68.5535 16.2791 67.1944L17.2819 60.5415C17.419 59.6324 17.052 58.7205 16.3233 58.1597L10.9914 54.0563C9.90221 53.2181 9.6816 51.6635 10.4946 50.5553L14.4744 45.1305C15.0182 44.3892 15.117 43.4112 14.7325 42.5761L11.9182 36.4648C11.3433 35.2164 11.8704 33.7374 13.1053 33.1339L19.1503 30.18C19.9763 29.7763 20.5183 28.9562 20.5659 28.0381L20.914 21.319C20.9851 19.9464 22.1392 18.8817 23.5131 18.9213L30.2384 19.1149C31.1574 19.1414 32.0184 18.6671 32.4873 17.8762L35.918 12.0886Z"
          fill="#FFB700"
        />
      </g>
      <path
        d="M30.1887 35.0105L33.3159 34.1726C34.3835 33.8865 35.2294 33.9291 35.8539 34.3002C36.4749 34.6588 36.9234 35.353 37.1994 36.3828L37.3407 36.9103C37.5224 37.5885 37.5575 38.1714 37.4458 38.659C37.3467 39.1432 37.0963 39.5401 36.6947 39.8496L36.7048 39.8873C37.8289 39.963 38.5895 40.7418 38.9866 42.2238L39.2894 43.3542C39.562 44.3715 39.5006 45.2225 39.1053 45.9072C38.7191 46.576 38.0111 47.0484 36.9813 47.3244L33.7222 48.1977L30.1887 35.0105ZM34.5097 39.6072C34.9241 39.4962 35.2032 39.307 35.3469 39.0396C35.5032 38.7689 35.5141 38.3824 35.3795 37.88L35.1826 37.1453C35.0547 36.6681 34.8743 36.3462 34.6412 36.1798C34.4208 36.0101 34.1284 35.974 33.7642 36.0716L32.7657 36.3391L33.6996 39.8243L34.5097 39.6072ZM36.4765 45.4405C36.8407 45.3429 37.0855 45.1764 37.2109 44.9409C37.3328 44.6928 37.3299 44.3302 37.202 43.8529L36.8941 42.7037C36.7326 42.1009 36.5148 41.715 36.2407 41.5462C35.9758 41.3614 35.6047 41.333 35.1275 41.4608L34.2044 41.7082L35.2897 45.7585L36.4765 45.4405ZM37.6168 33.0202L43.2684 31.5058L43.7732 33.3897L40.1938 34.3488L41.1277 37.834L43.9723 37.0717L44.4771 38.9556L41.6324 39.7178L42.7177 43.7682L46.2971 42.8091L46.8019 44.693L41.1503 46.2073L37.6168 33.0202ZM50.5869 43.8807C49.5821 44.1499 48.7466 44.0709 48.0802 43.6437C47.4105 43.204 46.9326 42.4504 46.6466 41.3828L46.4446 40.6293L48.4039 40.1043L48.6462 41.0086C48.875 41.8626 49.3474 42.1937 50.0632 42.0019C50.4149 41.9076 50.6517 41.7365 50.7737 41.4884C50.9049 41.2245 50.9082 40.8601 50.7837 40.3954C50.6356 39.8428 50.3805 39.3929 50.0182 39.0458C49.6526 38.6861 49.0517 38.302 48.2153 37.8934C47.1594 37.3687 46.3811 36.8503 45.8804 36.3384C45.3764 35.8138 45.0218 35.1685 44.8165 34.4024C44.5372 33.36 44.5856 32.4855 44.9616 31.779C45.3343 31.0599 46.023 30.5658 47.0278 30.2966C48.0199 30.0307 48.8446 30.1194 49.5018 30.5625C50.1681 30.9897 50.6443 31.737 50.9304 32.8046L51.0768 33.3509L49.1175 33.8759L48.9358 33.1977C48.8147 32.7455 48.6393 32.4425 48.4096 32.2887C48.1766 32.1223 47.8905 32.0846 47.5514 32.1754C46.8606 32.3605 46.628 32.8738 46.8535 33.7152C46.9813 34.1925 47.2264 34.6047 47.5886 34.9518C47.9634 35.2956 48.569 35.6718 49.4054 36.0804C50.4739 36.6017 51.2538 37.1263 51.7453 37.6542C52.2367 38.1821 52.5935 38.8605 52.8156 39.6895C53.105 40.7695 53.0571 41.6708 52.6718 42.3932C52.2991 43.1123 51.6041 43.6081 50.5869 43.8807ZM53.2548 30.8491L51.0884 31.4296L50.5836 29.5457L56.9888 27.8295L57.4935 29.7133L55.3271 30.2938L58.3558 41.5971L56.2835 42.1524L53.2548 30.8491ZM31.0194 53.4229L33.1105 52.8626L37.2079 62.7286L37.2456 62.7185L35.861 52.1256L37.7637 51.6158L39.3003 65.338L36.5498 66.075L31.0194 53.4229ZM39.6384 51.1134L42.4454 50.3613L48.1265 62.973L46.0543 63.5283L44.9758 61.0106L44.9859 61.0483L42.6311 61.6793L42.9459 64.3611L41.0243 64.876L39.6384 51.1134ZM44.2615 59.3242L41.5918 53.0534L41.5542 53.0635L42.3964 59.824L44.2615 59.3242ZM45.41 49.5669L47.4823 49.0117L50.511 60.3149L53.9208 59.4013L54.4256 61.2852L48.9435 62.7541L45.41 49.5669ZM58.2956 60.4501C57.2909 60.7193 56.4491 60.642 55.7702 60.2182C55.0879 59.7818 54.6037 59.0299 54.3177 57.9623L51.6221 47.9024L53.6944 47.3472L56.4303 57.5578C56.5515 58.0099 56.7269 58.3129 56.9565 58.4667C57.1988 58.6172 57.4894 58.647 57.8285 58.5561C58.1676 58.4653 58.3982 58.2958 58.5202 58.0477C58.6547 57.7963 58.6614 57.4445 58.5403 56.9924L55.8043 46.7818L57.8012 46.2467L60.4968 56.3066C60.7828 57.3742 60.7395 58.2675 60.3668 58.9865C59.9908 59.693 59.3004 60.1809 58.2956 60.4501ZM59.0887 45.9017L64.7404 44.3874L65.2452 46.2713L61.6658 47.2304L62.5996 50.7155L65.4443 49.9533L65.9491 51.8372L63.1044 52.5994L64.1897 56.6497L67.7691 55.6907L68.2739 57.5745L62.6222 59.0889L59.0887 45.9017Z"
        fill="black"
      />
      <defs>
        <filter
          id="filter0_d_528_5821"
          x="10.0049"
          y="10.5117"
          width="76.9711"
          height="76.6343"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="2.52965" dy="2.52965" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_528_5821"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_528_5821"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function EmailModal({
  open,
  onClose,
  planKey,
  customAmount,
  redirectPath
}) {
  const router = useRouter();
  const { query } = router;
  const pathname = usePathname();

  const { slug: _, ...restQuery } = query;
  const [form] = Form.useForm();

  const [getIsExistingUser, { loading }] = useLazyQuery(GET_IS_EXISTING_USER, {
    fetchPolicy: 'no-cache',
    onCompleted(response) {
      if (response?.emailExist) {
        const { isEmailExist } = response?.emailExist;

        const email = form.getFieldValue('email');
        router.push({
          pathname: isEmailExist ? ROUTES.LOGIN : ROUTES.SIGN_UP,

          query: {
            email,
            q: encode(
              `${ROUTES.MANAGE_PLAN}/${encode(planKey)}/payment-details`
            ),
            ...(customAmount && {
              token: encode(Number(customAmount))
            }),
            ...restQuery,
            redirectPath: redirectPath ?? pathname
          }
        });
      }
    },
    onError() {},
    client: authClient
  });

  const handleSubmit = ({ email }) => {
    if (!planKey) return;
    getIsExistingUser({
      variables: {
        data: { email }
      }
    });
  };
  return (
    <Modal
      className="bg-ivory cta-modal cancel-subscription-modal"
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      destroyOnClose
    >
      <div>
        <h3 className="display-h3 text-black-90 mb-4">Enter your email</h3>
        <p className="paragraph-m mb-32 text-black-70">
          We’ll create an account if you’re new, or ask you to log in.
        </p>
        <Form
          initialValues={initialEmailModalValues}
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input your email!' },
              {
                type: 'email',
                message: 'Please enter a valid email address!'
              }
            ]}
            normalize={(value) => value && value.toLowerCase()}
          >
            <Input type="email" />
          </Form.Item>

          <Flex gap={8} vertical className="cancel-subscription-btn mt-12">
            <Button
              className="cs-button"
              type="primary"
              size="large"
              block
              htmlType="submit"
              loading={loading}
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </div>
    </Modal>
  );
}

function CancelSubscriptionSuccessModal({ open, onClose }) {
  return (
    <Modal
      className="bg-ivory cta-modal cancel-subscription-modal"
      open={open}
      closable
      width={480}
      footer={null}
      onCancel={onClose}
      destroyOnClose
    >
      <div className="text-left">
        <p className="paragraph-m mb-16 text-black-90">
          You are now a Free Plan subscriber.
        </p>
        <Flex gap={8} className="cancel-subscription-btn">
          <Button
            className="cs-button mt-2"
            type="primary"
            size="large"
            block
            htmlType="submit"
            onClick={() => window.open('https://breitbart.com', '_self')}
          >
            Continue Browsing
          </Button>
        </Flex>
      </div>
    </Modal>
  );
}

function CancelSubscriptionModal({
  open,
  onClose,
  content,
  currentPlan,
  onSuccess
}) {
  const {
    state: { currentUser, accessToken }
  } = useApp();
  const { trackEvent } = useMatomo();
  const category = MATOMO_CATEGORY.SUBSCRIPTION;
  const action = MATOMO_ACTION.CLICK;
  const statusSuccess = TRACK_VALUE.SUCCESS;
  const statusFail = TRACK_VALUE.FAIL;
  const device = useDeviceInfo();
  const networkType = useNetworkType();
  const router = useRouter();

  const formContentDimension = () => {
    const userDimensions = getUserCommonDimensions(
      currentUser,
      device,
      networkType
    );
    const contentObjectForVideo = [...userDimensions];
    return contentObjectForVideo;
  };

  const [cancelSubscription, { loading }] = useMutation(CANCEL_SUBSCRIPTION, {
    onError() {
      trackEvent({
        category,
        action,
        name: MATOMO_NAME.SUBSCRIPTION_CANCEL,
        customDimensions: formContentDimension(),
        value: statusFail
      });
      gaEvent({
        category,
        action: MATOMO_ACTION.SUBSCRIPTION_CANCEL,
        name: JSON.stringify({
          content,
          currentPlan
        }),
        value: statusFail
      });
    },
    onCompleted(response) {
      if (response?.cancelSubscription) {
        trackEvent({
          category,
          action,
          name: MATOMO_NAME.SUBSCRIPTION_CANCEL,
          customDimensions: formContentDimension(),
          value: statusSuccess
        });
        gaEvent({
          category,
          action,
          name: JSON.stringify({
            content,
            currentPlan
          }),
          value: statusSuccess
        });
        onSuccess();
      }
    },
    context: {
      headers: {
        token: accessToken
      }
    }
  });

  return (
    <Modal
      className="bg-ivory cta-modal cancel-subscription-modal"
      open={open}
      closable={false}
      width={480}
      footer={null}
      onCancel={onClose}
      destroyOnClose
    >
      <div className="text-left">
        <h3 className="heading-h5 text-black-90 mb-32">
          Are you sure you want to cancel your subscription?
        </h3>
        <p className="paragraph-m mb-16 text-black-90">
          You’ll lose these great benefits:
        </p>
        <div
          className="flex-grow-1 subscription-benefits"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: content
          }}
        />
        <Flex gap={8} vertical className="cancel-subscription-btn">
          <Button
            className="cs-button mt-2"
            type="primary"
            size="large"
            block
            htmlType="submit"
            loading={loading}
            onClick={() => {
              trackEvent({
                category: MATOMO_CATEGORY.BUTTON,
                action: MATOMO_ACTION.CLICK,
                name: MATOMO_NAME.BUTTON_CLICK,
                customDimensions: [
                  {
                    id: actionDimensionIds.cta_title,
                    value: 'Continue with the cancellation'
                  },
                  { id: actionDimensionIds.page_source, value: router.asPath }
                ]
              });
              cancelSubscription();
            }}
          >
            Continue with the cancellation
          </Button>
          <Button
            size="large"
            block
            htmlType="button"
            className="cs-button"
            type="default"
            onClick={() => {
              trackEvent({
                category: MATOMO_CATEGORY.BUTTON,
                action: MATOMO_ACTION.CLICK,
                name: MATOMO_NAME.BUTTON_CLICK,
                customDimensions: [
                  {
                    id: actionDimensionIds.cta_title,
                    value: 'Keep subscription'
                  },
                  { id: actionDimensionIds.page_source, value: router.asPath }
                ]
              });
              onClose();
            }}
          >
            Keep subscription
          </Button>
        </Flex>
      </div>
    </Modal>
  );
}

export function UpdatePlanModal({
  open,
  onClose,
  planKey,
  customAmount,
  plans,
  onSuccess,
  handleRecommendedPlan,
  reActivate = false,
  endDate
}) {
  const {
    state: { accessToken, currentUser }
  } = useApp();
  const { trackEvent } = useMatomo();
  const category = MATOMO_CATEGORY.SUBSCRIPTION;
  const action = MATOMO_ACTION.CLICK;
  const statusSuccess = TRACK_VALUE.SUCCESS;
  const statusFail = TRACK_VALUE.FAIL;
  const device = useDeviceInfo();
  const networkType = useNetworkType();
  const router = useRouter();

  const activePlan = useMemo(
    () => currentUser?.currentSubscriptionPlan || {},
    [currentUser]
  );
  const selectedPlan = useMemo(
    () => plans?.find((plan) => plan?.key === planKey) ?? {},
    [planKey, plans]
  );

  const isUpgrade =
    Math.floor(selectedPlan?.weight) > Math.floor(activePlan?.planWeight);

  const formContentDimension = () => {
    const userDimensions = getUserCommonDimensions(
      currentUser,
      device,
      networkType
    );
    const contentObjectForVideo = [
      {
        id: actionDimensionIds.user_signup_method,
        value: SIGNUP_METHOD.EMAIL
      },
      ...userDimensions
    ];
    return contentObjectForVideo;
  };

  const [updateSubscription, { loading: updateLoading }] = useMutation(
    UPDATE_SUBSCRIPTION,
    {
      onError() {
        trackEvent({
          category,
          action,
          name: MATOMO_NAME.SUBSCRIPTION_FAILED,
          customDimensions: formContentDimension(),
          value: statusFail
        });
        logSubscriptionEvent({
          category,
          action: MATOMO_ACTION.SUBSCRIPTION_UPDATE,
          plan: planKey,
          amount: customAmount,
          value: statusFail
        });
      },
      onCompleted(response) {
        if (response) {
          trackEvent({
            category,
            action,
            name: isUpgrade
              ? MATOMO_NAME.SUBSCRIPTION_UPGRADE
              : MATOMO_NAME.SUBSCRIPTION_DOWNGRADE,
            customDimensions: formContentDimension(),
            value: statusSuccess
          });
          logSubscriptionEvent({
            category,
            action: MATOMO_ACTION.SUBSCRIPTION_UPDATE,
            plan: planKey,
            amount: customAmount,
            value: statusFail
          });
          onSuccess();
        }
      }
    }
  );

  const {
    data: { previewSubscriptionChange } = {},
    loading: loadingSubscriptionPreview
  } = useQuery(PREVIEW_SUBSCRIPTION, {
    fetchPolicy: 'network-only',
    variables: {
      data: {
        ...(customAmount && {
          amount: Number(customAmount)
        }),
        key: planKey
      }
    },
    context: {
      headers: {
        token: accessToken
      }
    },
    skip: !accessToken || !open || !planKey || reActivate === true
  });

  const unSelectedPlan = useMemo(
    () => plans?.find((plan) => plan?.key !== planKey) ?? {},
    [planKey, plans]
  );

  const { planName: name } = activePlan;
  const { name: newPlan } = selectedPlan;

  const isSamePlan =
    Math.floor(selectedPlan?.weight) === Math.floor(activePlan?.planWeight);

  const isFrequencySame = activePlan.frequency === selectedPlan.frequency;
  const isLoading = loadingSubscriptionPreview;

  const formatPrice = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }),
    []
  );

  const effectiveDate = useMemo(() => {
    if (
      name !== newPlan &&
      isUpgrade &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          The change to monthly billing will take effect{' '}
          <strong>immediately</strong>, but your current annual subscription has
          unused value that will be forfeited if you switch to the monthly
          billing option
        </>
      );
    }
    if (
      name !== newPlan &&
      !isUpgrade &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          The downgrade to monthly billing will take effect at the end of your
          current annual billing period on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
        </>
      );
    }
    if (
      name !== newPlan &&
      activePlan?.frequency === FREQUENCIES.MONTHLY &&
      selectedPlan?.frequency === FREQUENCIES.ANNUALLY
    ) {
      return (
        <>
          The change to annual billing will take effect{' '}
          <strong>immediately</strong>
        </>
      );
    }
    if (isUpgrade && isFrequencySame) {
      return (
        <>
          The upgrade will take effect <strong>immediately</strong>
        </>
      );
    }
    if (isSamePlan) {
      if (
        activePlan?.frequency === FREQUENCIES.MONTHLY &&
        selectedPlan?.frequency === FREQUENCIES.ANNUALLY
      ) {
        return (
          <>
            The change to annual billing will take effect{' '}
            <strong>immediately.</strong>
          </>
        );
      }
      if (
        activePlan?.frequency === FREQUENCIES.ANNUALLY &&
        selectedPlan?.frequency === FREQUENCIES.MONTHLY
      ) {
        return (
          <>
            The downgrade to monthly billing will take effect at the end of your
            current annual billing period on{' '}
            <strong>
              {dayjs(previewSubscriptionChange?.dueDate).format(
                'MMM DD, YYYY hh:mm A'
              )}
            </strong>
            .
          </>
        );
      }
    }
    if (!isUpgrade && !isSamePlan && isFrequencySame) {
      return (
        <>
          Your current plan will continue until the end of your current billing
          cycle on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
          .
        </>
      );
    }
  }, [
    isUpgrade,
    isSamePlan,
    activePlan,
    selectedPlan,
    isFrequencySame,
    previewSubscriptionChange,
    name,
    newPlan
  ]);

  const chargeToday = useMemo(() => {
    if (
      name !== newPlan &&
      isUpgrade &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          You will be charged{' '}
          <strong>
            {formatPrice.format(Math.abs(previewSubscriptionChange?.amountDue))}
          </strong>{' '}
          today for the first month of the new monthly plan
        </>
      );
    }
    if (
      name !== newPlan &&
      activePlan?.frequency === FREQUENCIES.MONTHLY &&
      selectedPlan?.frequency === FREQUENCIES.ANNUALLY
    ) {
      return (
        <>
          You will be charged{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}{' '}
          </strong>
          today minus a prorated credit of{' '}
          {formatPrice.format(
            Math.abs(previewSubscriptionChange?.unusedAmount)
          )}{' '}
          for the unused portion of your current monthly subscription.
        </>
      );
    }
    if (isUpgrade) {
      return (
        <>
          You will be charged{' '}
          <strong>
            {formatPrice.format(Math.abs(previewSubscriptionChange?.amountDue))}{' '}
          </strong>
          today (prorated difference for the remaining days of the current
          billing cycle).
        </>
      );
    }
    if (isSamePlan) {
      if (
        activePlan?.frequency === FREQUENCIES.MONTHLY &&
        selectedPlan?.frequency === FREQUENCIES.ANNUALLY
      ) {
        return (
          <>
            You will be charged{' '}
            <strong>
              {formatPrice.format(
                Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
              )}{' '}
            </strong>
            today minus a prorated credit of{' '}
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.unusedAmount)
            )}{' '}
            for the unused portion of your current monthly subscription.
          </>
        );
      }
      if (
        activePlan?.frequency === FREQUENCIES.ANNUALLY &&
        selectedPlan?.frequency === FREQUENCIES.MONTHLY
      ) {
        return `No charges will be applied today`;
      }
    }
    if (!isUpgrade && !isSamePlan) {
      return `No charges will be applied today`;
    }
  }, [
    isUpgrade,
    isSamePlan,
    activePlan,
    selectedPlan,
    previewSubscriptionChange,
    name,
    newPlan,
    formatPrice
  ]);

  const nextBillingDate = useMemo(() => {
    if (
      name !== newPlan &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          Your new plan will take effect on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
          .
        </>
      );
    }
    if (
      name !== newPlan &&
      activePlan?.frequency === FREQUENCIES.MONTHLY &&
      selectedPlan?.frequency === FREQUENCIES.ANNUALLY
    ) {
      return (
        <>
          Your next billing will occur <strong>one year</strong> from today on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
        </>
      );
    }
    if (isUpgrade) {
      return (
        <>
          Your next billing will occur on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
        </>
      );
    }
    if (isSamePlan) {
      if (
        activePlan?.frequency === FREQUENCIES.MONTHLY &&
        selectedPlan?.frequency === FREQUENCIES.ANNUALLY
      ) {
        return (
          <>
            Your next billing will occur <strong>one year</strong> from today on{' '}
            <strong>
              {dayjs(previewSubscriptionChange?.dueDate).format(
                'MMM DD, YYYY hh:mm A'
              )}
            </strong>
            .
          </>
        );
      }
      if (
        activePlan?.frequency === FREQUENCIES.ANNUALLY &&
        selectedPlan?.frequency === FREQUENCIES.MONTHLY
      ) {
        return (
          <>
            Your new plan will take effect on{' '}
            <strong>
              {dayjs(previewSubscriptionChange?.dueDate).format(
                'MMM DD, YYYY hh:mm A'
              )}
            </strong>
            .
          </>
        );
      }
    }
    if (!isUpgrade && !isSamePlan) {
      return (
        <>
          {' '}
          Your new plan will take effect on{' '}
          <strong>
            {dayjs(previewSubscriptionChange?.dueDate).format(
              'MMM DD, YYYY hh:mm A'
            )}
          </strong>
        </>
      );
    }
  }, [
    name,
    newPlan,
    isUpgrade,
    isSamePlan,
    activePlan,
    selectedPlan,
    previewSubscriptionChange
  ]);

  const newPlanPrice = useMemo(() => {
    if (
      name !== newPlan &&
      isUpgrade &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          You will now be billed monthly at{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}
          </strong>
          .
        </>
      );
    }
    if (
      name !== newPlan &&
      !isUpgrade &&
      activePlan?.frequency === FREQUENCIES.ANNUALLY &&
      selectedPlan?.frequency === FREQUENCIES.MONTHLY
    ) {
      return (
        <>
          You will be Billed{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}
          </strong>{' '}
          per month starting on your next billing date. .
        </>
      );
    }
    if (
      name !== newPlan &&
      activePlan?.frequency === FREQUENCIES.MONTHLY &&
      selectedPlan?.frequency === FREQUENCIES.ANNUALLY
    ) {
      return (
        <>
          You will now be billed annually at{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}
          </strong>
        </>
      );
    }
    if (isUpgrade) {
      return (
        <>
          Starting from your next billing date, your new plan will be{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}{' '}
          </strong>
          per{' '}
          {selectedPlan?.frequency === FREQUENCIES.ANNUALLY ? 'year' : 'month'}.
        </>
      );
    }
    if (isSamePlan) {
      if (
        activePlan?.frequency === FREQUENCIES.MONTHLY &&
        selectedPlan?.frequency === FREQUENCIES.ANNUALLY
      ) {
        return (
          <>
            You will now be billed annually at{' '}
            <strong>
              {formatPrice.format(
                Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
              )}
            </strong>
          </>
        );
      }
      if (
        activePlan?.frequency === FREQUENCIES.ANNUALLY &&
        selectedPlan?.frequency === FREQUENCIES.MONTHLY
      ) {
        return (
          <>
            You will be billed{' '}
            <strong>
              {formatPrice.format(
                Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
              )}{' '}
            </strong>
            per month starting on your next billing date.
          </>
        );
      }
    }
    if (!isUpgrade && !isSamePlan) {
      return (
        <>
          Starting from your next billing date, your new plan will be{' '}
          <strong>
            {formatPrice.format(
              Math.abs(previewSubscriptionChange?.taxDetails?.totalAmount)
            )}{' '}
          </strong>
          per{' '}
          {selectedPlan?.frequency === FREQUENCIES.ANNUALLY ? 'year' : 'month'}.
        </>
      );
    }
  }, [
    name,
    newPlan,
    isUpgrade,
    isSamePlan,
    activePlan,
    selectedPlan,
    previewSubscriptionChange,
    formatPrice
  ]);

  const handleUpdate = async () => {
    updateSubscription({
      variables: {
        data: {
          ...(customAmount && {
            amount: Number(customAmount)
          }),
          key: planKey
        }
      },
      context: {
        headers: {
          token: accessToken
        }
      }
    });
  };

  return (
    <Modal
      className="bg-ivory cta-modal cancel-subscription-modal"
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      destroyOnClose
    >
      {reActivate ? (
        <div className="text-left">
          <h3 className="heading-h5 text-black-90 mb-32">
            You are already subscribed until {endDate}. Would you like to
            reactivate your subscription so that you don't lose access at the
            end of your current access period?
          </h3>

          <Flex gap={8} vertical className="cancel-subscription-btn">
            <Button
              className="cs-button mt-2"
              type="primary"
              size="large"
              block
              htmlType="submit"
              onClick={() => {
                trackEvent({
                  category: MATOMO_CATEGORY.BUTTON,
                  action: MATOMO_ACTION.CLICK,
                  name: MATOMO_NAME.BUTTON_CLICK,
                  customDimensions: [
                    { id: actionDimensionIds.cta_title, value: 'Yes' },
                    {
                      id: actionDimensionIds.page_source,
                      value: router.asPath
                    }
                  ]
                });
                handleUpdate();
              }}
            >
              Yes
            </Button>
            <Button
              size="large"
              block
              htmlType="button"
              className="cs-button"
              type="default"
              onClick={onClose}
            >
              No
            </Button>
          </Flex>
        </div>
      ) : (
        <div>
          <h3 className="display-h3 text-black-90 mb-4">Plan Change Summary</h3>
          <div className="mt-24 text-black-90">
            {isLoading && <Spin />}
            {!isLoading && (
              <>
                <div className="mb-32 text-tiny">
                  <p className="mb-8 d-flex align-center ">
                    <strong>
                      {`${name} (${activePlan.frequency})`}{' '}
                      <ArrowRight size={16} />{' '}
                      {`${newPlan} (${selectedPlan.frequency})`}
                    </strong>
                  </p>
                  <p className="mb-8">
                    <strong>Effective Date: </strong>
                    {effectiveDate}
                  </p>{' '}
                  {name !== newPlan &&
                    isUpgrade &&
                    activePlan?.frequency === FREQUENCIES.ANNUALLY &&
                    selectedPlan?.frequency === FREQUENCIES.MONTHLY && (
                      <p className="mb-8">
                        <strong>Why No Credit Is Applied:</strong> Because you
                        are switching from annual to monthly billing, the unused
                        value of your current subscription will not be credited
                        to your new monthly plan.
                      </p>
                    )}
                  {name !== newPlan &&
                    isUpgrade &&
                    activePlan?.frequency === FREQUENCIES.ANNUALLY &&
                    selectedPlan?.frequency === FREQUENCIES.MONTHLY && (
                      <p className="mb-8">
                        <strong>Upgrade Option:</strong>To avoid forfeiting the
                        unused value of your current annual subscription, you
                        can switch to the{' '}
                        <strong>annual version of your new plan</strong> and
                        receive a prorated credit based on what you’ve already
                        paid.
                      </p>
                    )}
                  {name !== newPlan &&
                    isUpgrade &&
                    activePlan?.frequency === FREQUENCIES.ANNUALLY &&
                    selectedPlan?.frequency === FREQUENCIES.MONTHLY && (
                      <Button
                        className="cs-button mt-2 mb-8"
                        type="primary"
                        size="large"
                        block
                        onClick={() => {
                          trackEvent({
                            category: MATOMO_CATEGORY.BUTTON,
                            action: MATOMO_ACTION.CLICK,
                            name: MATOMO_NAME.BUTTON_CLICK,
                            customDimensions: [
                              {
                                id: actionDimensionIds.cta_title,
                                value: `Switch to ${selectedPlan?.name} annual`
                              },
                              {
                                id: actionDimensionIds.page_source,
                                value: router.asPath
                              }
                            ]
                          });
                          handleRecommendedPlan(unSelectedPlan?.key);
                        }}
                      >
                        Switch to {selectedPlan?.name} annual
                      </Button>
                    )}
                  {name !== newPlan &&
                    isUpgrade &&
                    activePlan?.frequency === FREQUENCIES.ANNUALLY &&
                    selectedPlan?.frequency === FREQUENCIES.MONTHLY && (
                      <p className="mb-8">
                        <strong>
                          Alternatively, Switch to Monthly Billing:
                        </strong>
                      </p>
                    )}
                  {name !== newPlan &&
                    isUpgrade &&
                    activePlan?.frequency === FREQUENCIES.ANNUALLY &&
                    selectedPlan?.frequency === FREQUENCIES.MONTHLY && (
                      <p className="mb-8">
                        <strong>Effective Date: </strong>The downgrade to
                        monthly billing will take effect immediately, and your
                        current annual subscription’s unused value will be
                        forfeited.
                      </p>
                    )}
                  <p className="mb-8">
                    <strong>Charge Today: </strong>
                    {chargeToday}
                  </p>
                  {activePlan?.frequency === FREQUENCIES.MONTHLY &&
                    selectedPlan?.frequency === FREQUENCIES.ANNUALLY && (
                      <p className="mb-8">
                        <strong>Total Charge Today:</strong>

                        <strong>
                          {formatPrice.format(
                            Math.abs(previewSubscriptionChange?.amountDue)
                          )}
                        </strong>
                      </p>
                    )}
                  <p className="mb-8">
                    <strong>Next Billing Date: </strong>
                    {nextBillingDate}
                  </p>
                  <p className="mb-8">
                    <strong>New Plan Price: </strong>
                    {newPlanPrice}
                  </p>
                </div>
                <Flex gap={8} vertical className="cancel-subscription-btn">
                  <Button
                    className="cs-button mt-2"
                    type="primary"
                    size="large"
                    block
                    htmlType="submit"
                    onClick={handleUpdate}
                    loading={isLoading || updateLoading}
                  >
                    Update Plan
                  </Button>
                  <Button
                    size="large"
                    block
                    htmlType="button"
                    className="cs-button"
                    type="default"
                    onClick={onClose}
                  >
                    Keep subscription
                  </Button>
                </Flex>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
function CancelledText({ date }) {
  return (
    <span className="cancelled-text">Cancelled - Active until {date}</span>
  );
}

function TaxContent() {
  return (
    <div className="cancel-text">
      Prices listed do not include applicable taxes. Taxes may be applied at
      checkout based on your location
    </div>
  );
}

function SubscriptionPlan({
  name,
  data = [],
  handleRedirect,
  loadingLogin,
  frequency
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { trackEvent } = useMatomo();
  const {
    state: { currentUser: user, isUnAuthenticated, loading, uiLabels },
    updateSession
  } = useApp();
  const validStatuses = [
    SUBSCRIPTION_STATUS.ACTIVE,
    SUBSCRIPTION_STATUS.PENDING,
    SUBSCRIPTION_STATUS.PAYMENT_DUE,
    SUBSCRIPTION_STATUS.CANCELLED_AT_PERIOD_END
  ];

  const isLoading = loading || loadingLogin;

  const [emailModalState, setEmailModalState] = useState({
    open: false,
    planKey: ''
  });

  const [updatePlanModalState, setUpdatePlanModalState] = useState({
    open: false,
    planKey: ''
  });

  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] =
    useState(false);

  const [openCancelSuccessModal, setOpenCancelSuccessModal] = useState(false);

  const monthlyPlan = useMemo(
    () => data.find((plan) => plan?.frequency === FREQUENCIES.MONTHLY),
    [data]
  );

  const yearlyPlan = useMemo(
    () => data.find((plan) => plan?.frequency === FREQUENCIES.ANNUALLY),
    [data]
  );

  const monthlyPriceDisplay = currencyFormat(
    monthlyPlan?.amount?.usd?.price ?? 0
  );
  const yearlyPriceDisplay = currencyFormat(
    (yearlyPlan?.amount?.usd?.price ?? 0) / 12
  );

  const device = useDeviceInfo();
  const networkType = useNetworkType();

  const formContentDimension = () => {
    const userDimensions = getUserCommonDimensions(user, device, networkType);
    const contentObjectForVideo = [
      { id: actionDimensionIds.user_signup_method, value: SIGNUP_METHOD.EMAIL },
      {
        id: actionDimensionIds.subscription_initiate_source,
        value: router.asPath
      },
      ...userDimensions
    ];
    return contentObjectForVideo;
  };

  const isBestValue = [
    monthlyPlan?.name?.toLowerCase(),
    yearlyPlan?.name?.toLowerCase()
  ].includes('MiddleWeight'.toLowerCase());

  const content = monthlyPlan?.html ?? yearlyPlan?.html;
  const currentPlan = user?.currentSubscriptionPlan;
  const currentPlanAmount = currentPlan
    ? currentPlan?.amount /
      (currentPlan?.frequency === FREQUENCIES.ANNUALLY ? 12 : 1)
    : 0;

  const currentPlanKey = user?.currentPlan;
  const hasActivePlan =
    currentPlanKey && currentPlanKey !== SUBSCRIPTION_PLAN_KEYS.FREE;
  const isHeavyWeighPlan =
    monthlyPlan?.key === SUBSCRIPTION_PLAN_KEYS.HEAVYWEIGHT_MONTHLY;
  const headerImage = isHeavyWeighPlan
    ? uiLabels?.SUBSCRIPTION_PLAN_HEAVYWEIGHT_HEADER_IMAGE?.value
    : '';
  const hasActiveHeavyWeightPlan =
    hasActivePlan &&
    [
      SUBSCRIPTION_PLAN_KEYS.HEAVYWEIGHT_ANNUALLY,
      SUBSCRIPTION_PLAN_KEYS.HEAVYWEIGHT_MONTHLY
    ].includes(currentPlanKey);
  const isPlanCancelledAtPeriod =
    user?.currentSubscriptionPlan?.status ===
    SUBSCRIPTION_STATUS.CANCELLED_AT_PERIOD_END;
  const isSubscriptionDowngraded =
    user?.currentSubscriptionPlan?.isSubscriptionDowngraded;

  const isMonthlyPlanActive =
    currentPlanKey && currentPlanKey === monthlyPlan?.key;
  const isYearlyPlanActive =
    currentPlanKey && currentPlanKey === yearlyPlan?.key;

  const monthlyPlanButtonText = useMemo(() => {
    if (isLoading) return '';
    const amount = monthlyPlan?.amount?.usd?.price ?? 0;
    if (isMonthlyPlanActive) {
      return 'Your Plan';
    }
    if (currentPlan?.id) {
      if (
        isSubscriptionDowngraded &&
        user?.currentSubscriptionPlan?.upcomingPlanDetails?.name ===
          monthlyPlan?.name &&
        user?.currentSubscriptionPlan?.upcomingPlanDetails?.frequency ===
          'MONTHLY'
      ) {
        return `Upcoming plan`;
      }
      if (currentPlan?.planName === monthlyPlan?.name) {
        return 'Switch to this plan';
      }
      if (amount < currentPlanAmount) return 'Downgrade';
      return 'Upgrade';
    }
    return 'Subscribe';
  }, [
    isSubscriptionDowngraded,
    isMonthlyPlanActive,
    monthlyPlan,
    isLoading,
    currentPlanAmount,
    currentPlan,
    user
  ]);

  const yearlyPlanButtonText = useMemo(() => {
    if (isLoading) return '';
    const amount = yearlyPlan?.amount?.usd?.price / 12 ?? 0;
    if (isYearlyPlanActive) {
      return 'Your Plan';
    }
    if (currentPlan?.id) {
      if (
        isSubscriptionDowngraded &&
        user?.currentSubscriptionPlan?.upcomingPlanDetails?.name ===
          yearlyPlan?.name &&
        user?.currentSubscriptionPlan?.upcomingPlanDetails?.frequency ===
          FREQUENCIES.ANNUALLY
      ) {
        return `Upcoming plan`;
      }
      if (currentPlan?.planName === yearlyPlan?.name) {
        return 'Switch to this plan';
      }
      if (amount < currentPlanAmount) return 'Downgrade';
      return 'Upgrade';
    }
    return 'Subscribe';
  }, [
    isSubscriptionDowngraded,
    isYearlyPlanActive,
    yearlyPlan,
    isLoading,
    currentPlanAmount,
    currentPlan,
    user
  ]);

  const heavyWeightButtonText = useMemo(() => {
    if (isLoading) return '';
    if (hasActiveHeavyWeightPlan) return 'Change Subscription';
    if (hasActivePlan) return 'Upgrade';
    return 'Subscribe';
  }, [isLoading, hasActivePlan, hasActiveHeavyWeightPlan]);

  const heavyWeightButtonActivePlanText = useMemo(() => {
    const amount$ = user?.currentSubscriptionPlan?.amount;
    if (isYearlyPlanActive) {
      const amount = currencyFormat(
        (amount$ || yearlyPlan?.amount?.usd?.price) / 12
      );
      return `Your Plan ${amount}/month*`;
    }
    const amount = currencyFormat(amount$ || monthlyPlan?.amount?.usd?.price);
    return `Your Plan ${amount}/month`;
  }, [isYearlyPlanActive, yearlyPlan, monthlyPlan, user]);

  const isMonthlyPlanLoading = isLoading;
  const isYearlyPlanLoading = isLoading;

  const handleCancelSubscription = () => {
    setOpenCancelSubscriptionModal(true);
  };

  const handleUpdatePlanModalClose = () => {
    setUpdatePlanModalState({
      open: false,
      planKey: '',
      customAmount: null
    });
  };

  const handleSuccessCancelSubscription = () => {
    handleRedirect(async () => {
      await updateSession();
      setOpenCancelSubscriptionModal(false);
      setOpenCancelSuccessModal(true);
    });
  };

  const handleSuccessUpdateSubscription = () => {
    handleRedirect(async () => {
      await updateSession();
      handleUpdatePlanModalClose();
    });
  };

  const handleSubscribe = (plan) => {
    const key = plan?.key;
    if (!key) return;

    if (isUnAuthenticated) {
      trackEvent({
        category: MATOMO_CATEGORY.SUBSCRIPTION,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.SUBSCRIPTION_START,
        customDimensions: formContentDimension()
      });
      setEmailModalState({
        planKey: key,
        open: true
      });
      return;
    }
    if (hasActivePlan && user?.currentPlan === key) {
      setUpdatePlanModalState({
        open: true,
        reActivate: true,
        planKey: key,
        endDate: moment(user?.currentSubscriptionPlan?.expiredAt).format(
          DATE_FORMATS?.BILLING_MODULE
        )
      });
    } else if (hasActivePlan) {
      setUpdatePlanModalState({
        planKey: key,
        open: true
      });
    } else {
      trackEvent({
        category: MATOMO_CATEGORY?.SUBSCRIPTION,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.SUBSCRIPTION_START,
        customDimensions: formContentDimension()
      });
      const { token: _2, slug, ...q } = router.query;
      const query = {
        ...q,
        redirectPath: router?.query?.redirectPath || pathname
      };

      router.push({
        pathname: `${ROUTES.MANAGE_PLAN}/${encode(key)}/payment-details`,
        query
      });
    }
  };

  const handleEmailModalClose = () => {
    setEmailModalState({
      open: false,
      planKey: ''
    });
  };

  const handleHeavyWeightRedirect = () => {
    const { token: _2, slug, ...q } = router.query;
    const query = {
      ...q,
      redirectPath: router?.query?.redirectPath || pathname
    };

    router.push({
      pathname: `${ROUTES.CUSTOM_AMOUNT}`,
      query
    });
  };
  const handleRecommendedPlan = (planKey) => {
    setUpdatePlanModalState({
      open: true,
      planKey
    });
  };
  return (
    <>
      <UpdatePlanModal
        {...updatePlanModalState}
        handleRecommendedPlan={handleRecommendedPlan}
        onClose={handleUpdatePlanModalClose}
        plans={data}
        currentPlan={currentPlanKey}
        onSuccess={handleSuccessUpdateSubscription}
      />
      <EmailModal
        {...emailModalState}
        redirectPath={router?.query?.redirectPath}
        onClose={handleEmailModalClose}
      />
      {!isPlanCancelledAtPeriod &&
        !validStatuses.includes(user?.currentSubscriptionPlan?.status) && (
          <CancelSubscriptionSuccessModal
            open={openCancelSuccessModal}
            onClose={() => setOpenCancelSuccessModal(false)}
          />
        )}
      <CancelSubscriptionModal
        content={content}
        onClose={() => setOpenCancelSubscriptionModal(false)}
        open={openCancelSubscriptionModal}
        currentPlan={currentPlanKey}
        onSuccess={handleSuccessCancelSubscription}
      />
      <div
        className={`mp-v2-block${isBestValue ? ' mp-v2-best-value' : ''}`}
        id={name.toLowerCase()}
      >
        {isBestValue && <BestValue className="best-value-block" />}
        <div
          className={`mp-v2-header${headerImage ? ' mp-v2-header-image' : ''}`}
        >
          {headerImage ? (
            <Image
              src={headerImage}
              alt={name}
              width={310}
              height={94}
              priority
            />
          ) : (
            <h3 className="display-h3 text-white-90">{name}</h3>
          )}
        </div>
        <div className="mp-v2-body">
          {!isHeavyWeighPlan && (
            <>
              {frequency === FREQUENCIES.ANNUALLY && yearlyPlan && (
                <div className="mp-v2-price-line">
                  <span className="mp-v2-price">
                    {yearlyPriceDisplay}/month
                  </span>
                  <span className="mp-v2-cancel-inline">Cancel anytime.</span>
                </div>
              )}
              {frequency === FREQUENCIES.ANNUALLY && yearlyPlan && (
                <div className="primary-button text-right">
                  <Button
                    block
                    type="primary"
                    disabled={
                      (!isYearlyPlanActive && isPlanCancelledAtPeriod) || // if plan is in cancellation period but not active
                      isSubscriptionDowngraded || // if plan is downgraded
                      (isYearlyPlanActive && !isPlanCancelledAtPeriod) // if plan is active and not in cancellation period
                    }
                    className={
                      isYearlyPlanActive &&
                      isPlanCancelledAtPeriod &&
                      'gray-active-button'
                    }
                    loading={isYearlyPlanLoading}
                    onClick={() => {
                      trackEvent({
                        category: MATOMO_CATEGORY.BUTTON,
                        action: MATOMO_ACTION.CLICK,
                        name: MATOMO_NAME.BUTTON_CLICK,
                        customDimensions: [
                          {
                            id: actionDimensionIds.cta_title,
                            value: yearlyPlanButtonText
                          },
                          {
                            id: actionDimensionIds.page_source,
                            value: router.asPath
                          }
                        ]
                      });
                      handleSubscribe(yearlyPlan);
                    }}
                  >
                    {yearlyPlanButtonText}
                  </Button>
                  {isYearlyPlanActive && isPlanCancelledAtPeriod && (
                    <CancelledText
                      date={moment(
                        user?.currentSubscriptionPlan?.expiredAt
                      ).format(DATE_FORMATS.BILLING_MODULE)}
                    />
                  )}
                  <span className="mt-8 mp-v2-billed-annually">
                    *Billed Annually
                  </span>
                </div>
              )}
              {frequency === FREQUENCIES.ANNUALLY &&
                isYearlyPlanActive &&
                !isPlanCancelledAtPeriod && (
                  <div className="mp-v2-cancel-subscription">
                    <Button type="text" onClick={handleCancelSubscription}>
                      Cancel Subscription
                    </Button>
                  </div>
                )}
              {frequency === FREQUENCIES.MONTHLY && monthlyPlan && (
                <div className="mp-v2-price-line">
                  <span className="mp-v2-price">
                    {monthlyPriceDisplay}/month
                  </span>
                  <span className="mp-v2-cancel-inline">Cancel anytime.</span>
                </div>
              )}
              {frequency === FREQUENCIES.MONTHLY && monthlyPlan && (
                <div className="primary-button text-right">
                  <Button
                    block
                    type="primary"
                    disabled={
                      (!isMonthlyPlanActive && isPlanCancelledAtPeriod) ||
                      isSubscriptionDowngraded ||
                      (isMonthlyPlanActive && !isPlanCancelledAtPeriod)
                    }
                    className={
                      isMonthlyPlanActive &&
                      isPlanCancelledAtPeriod &&
                      'gray-active-button'
                    }
                    loading={isMonthlyPlanLoading}
                    onClick={() => handleSubscribe(monthlyPlan)}
                  >
                    {monthlyPlanButtonText}
                  </Button>
                  {isMonthlyPlanActive && isPlanCancelledAtPeriod && (
                    <CancelledText
                      date={moment(
                        user?.currentSubscriptionPlan?.expiredAt
                      ).format(DATE_FORMATS.BILLING_MODULE)}
                    />
                  )}
                </div>
              )}
              {frequency === FREQUENCIES.MONTHLY &&
                isMonthlyPlanActive &&
                !isPlanCancelledAtPeriod && (
                  <div className="mp-v2-cancel-subscription">
                    <Button type="text" onClick={handleCancelSubscription}>
                      Cancel Subscription
                    </Button>
                  </div>
                )}
              <div
                className="mp-v2-features"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: content
                }}
              />
              <TaxContent />
            </>
          )}
          {isHeavyWeighPlan && (
            <>
              {monthlyPlan?.description && (
                <p className="mp-v2-custom-amount-text">
                  {monthlyPlan?.description}
                </p>
              )}
              {!hasActiveHeavyWeightPlan && (
                <>
                  <div className="mp-v2-price-line">
                    <p className="mp-v2-amount-text">
                      Starts at{' '}
                      <span className="mp-v2-highlight">
                        {currencyFormat(monthlyPlan?.amount?.usd?.price)}/mo
                      </span>
                    </p>
                  </div>
                  <div className="primary-button text-right">
                    <Button
                      block
                      type="primary"
                      onClick={() => handleSubscribe(yearlyPlan)}
                      loading={isLoading}
                      disabled={
                        isSubscriptionDowngraded || isPlanCancelledAtPeriod
                      }
                    >
                      {heavyWeightButtonText}
                    </Button>
                  </div>
                  <div
                    className="mp-v2-features"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: content
                    }}
                  />
                  <TaxContent />
                </>
              )}
              {hasActiveHeavyWeightPlan && (
                <>
                  <div className="primary-button text-right">
                    <Button
                      block
                      type="primary"
                      disabled={
                        ((isYearlyPlanActive || isMonthlyPlanActive) &&
                          !isPlanCancelledAtPeriod) ||
                        isSubscriptionDowngraded ||
                        (!(isYearlyPlanActive || isMonthlyPlanActive) &&
                          isPlanCancelledAtPeriod)
                      }
                      loading={isYearlyPlanLoading || isMonthlyPlanLoading}
                      onClick={() => {
                        if (isPlanCancelledAtPeriod) {
                          handleSubscribe(
                            isYearlyPlanActive ? yearlyPlan : monthlyPlan
                          );
                        } else {
                          handleSubscribe(yearlyPlan);
                        }
                      }}
                      className={
                        isPlanCancelledAtPeriod && 'gray-active-button'
                      }
                    >
                      {heavyWeightButtonActivePlanText}
                    </Button>
                    {isPlanCancelledAtPeriod && (
                      <CancelledText
                        date={moment(
                          user?.currentSubscriptionPlan?.expiredAt
                        ).format(DATE_FORMATS.BILLING_MODULE)}
                      />
                    )}
                  </div>
                  {!isPlanCancelledAtPeriod && (
                    <div className="mp-v2-cancel-subscription">
                      <Button type="text" onClick={handleCancelSubscription}>
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                  <div className="primary-button text-right mb-12 mt-12">
                    <Button
                      block
                      type="primary"
                      onClick={handleHeavyWeightRedirect}
                      disabled={
                        isSubscriptionDowngraded || isPlanCancelledAtPeriod
                      }
                    >
                      {heavyWeightButtonText}
                    </Button>
                  </div>
                  <div
                    className="mp-v2-features"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: content
                    }}
                  />
                  <TaxContent />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export function SubscriptionPlans({ data = [], title, description, uiLabels }) {
  const {
    state: { loading, uiLabels: appUiLabels }
  } = useApp();
  const [logoutLoading, setLogOutLoading] = useState(false);
  const [frequency, setFrequency] = useState(FREQUENCIES.ANNUALLY);
  const router = useRouter();

  const {
    redirect,
    redirectToMobile,
    redirectURL,
    token,
    slug: _,
    ...restQuery
  } = router.query;

  const isRedirect = redirect === 'true' || redirectToMobile === 'true';

  const handleRedirect = (cb = () => {}) => {
    if (redirectURL) {
      window.location.href = decodeURIComponent(redirectURL);
      return;
    }
    if (isRedirect) {
      const redirectTo =
        uiLabels?.CALLBACK_URL?.value ||
        `${window.location.origin}${ROUTES.CALLBACK}`;
      const url = new URL(redirectTo);
      Object.entries(restQuery).forEach(([key, value]) =>
        url.searchParams.set(key, value)
      );
      window.open(url.toString(), '_self');
      return;
    }

    cb();
  };

  const onLogin = () => {};

  const onError = () => {
    router.push(ROUTES.LOGIN);
  };

  useEffect(() => {
    if (!token && redirect === 'true' && !loading) {
      (async () => {
        setLogOutLoading(true);
        await signOut({
          redirect: false
        });
        setLogOutLoading(false);
      })();
    }
  }, [redirect, token, loading]);

  const loadingLogin = useLoginWithToken(onLogin, onError);

  const plans = useMemo(
    () => Object.entries(groupBy(data, (item) => item?.name)),
    [data]
  );

  const isLoading = loadingLogin || logoutLoading || loading;
  const logoImage =
    appUiLabels?.SUBSCRIPTION_LIST_LOGO_IMAGE?.value ||
    uiLabels?.SUBSCRIPTION_LIST_LOGO_IMAGE?.value;

  return (
    <>
      {logoImage && (
        <div className="mp-v2-logo">
          <Image
            src={logoImage}
            alt=""
            width={110}
            height={32}
            style={{ width: 'auto', height: 32 }}
            priority
          />
        </div>
      )}
      {title && <h2 className="mp-v2-title display-h2 text-center">{title}</h2>}
      {description && (
        <p className="mp-v2-description text-center">{description}</p>
      )}
      <div className="mp-v2-frequency-toggle">
        <Segmented
          value={frequency}
          onChange={setFrequency}
          options={[
            { label: 'Monthly', value: FREQUENCIES.MONTHLY },
            { label: 'Annually', value: FREQUENCIES.ANNUALLY }
          ]}
        />
      </div>
      <div className="mp-v2-wrapper">
        {plans.map(([name, items]) => (
          <SubscriptionPlan
            name={name}
            key={name}
            data={items}
            handleRedirect={handleRedirect}
            loadingLogin={isLoading}
            frequency={frequency}
          />
        ))}
      </div>
    </>
  );
}

function SubscriptionList({ plans, title, description }) {
  const uiLabels = useStaticData(`${STATIC_DATA_KEYS.UI_LABELS}/en.json`);

  return (
    <section className="manage-plan-v2" id="manage-plan">
      <style>{`body, .ant-layout.layout { background-color: #ffffff !important; }`}</style>
      <div className="container">
        <SubscriptionPlans
          data={plans}
          title={title}
          description={description}
          uiLabels={uiLabels}
        />
      </div>
    </section>
  );
}

export default SubscriptionList;
